import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  setCredentials,
  setProject,
  setProjects,
} from "../../reducers/AuthSlice";
import { getProjects, logIn } from "../../api/FlaskAPI";
import { getErrorMessage } from "../../api/errors";
import logo from "../../assets/bluebell-logo.png";
import { LANGUAGES } from "../../i18n";

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const otherLanguage = LANGUAGES.find(
    (language) => language.value !== i18n.resolvedLanguage,
  );
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await logIn({ username, password });
      const { access_token, user } = response.data;
      dispatch(setCredentials({ user, token: access_token }));

      // Fetch available projects and default to the first one
      try {
        const projectsResponse = await getProjects();
        if (projectsResponse && projectsResponse.data) {
          const projectsData = projectsResponse.data;
          dispatch(setProjects(projectsData));

          const storedId = localStorage.getItem("projectId");
          const entries = Object.entries(projectsData);
          if (entries.length === 0) {
            throw new Error("no projects found");
          }

          let defaultProjectName = null;
          let defaultProjectId = null;

          if (storedId) {
            // Check if previously selected project still exists (values are ids)
            const existingEntry = entries.find(([_, id]) => id === storedId);
            if (existingEntry) {
              defaultProjectName = existingEntry[0];
              defaultProjectId = existingEntry[1];
            }
          }

          if (!defaultProjectName) {
            // Default to the first project
            const [firstName, firstId] = entries[0];
            defaultProjectName = firstName;
            defaultProjectId = firstId;
          }

          if (defaultProjectName && defaultProjectId) {
            dispatch(
              setProject({
                projectName: defaultProjectName,
                projectId: defaultProjectId,
              }),
            );
          }
        }
      } catch (projectsErr) {
        console.error("Error fetching projects:", projectsErr);
      }

      navigate("/");
    } catch (err) {
      setError(getErrorMessage(err, t("login.failed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        py: 4,
        background:
          "linear-gradient(160deg, rgba(var(--primary-rgb), 0.12), rgba(var(--primary-rgb), 0.02))",
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={3} sx={{ p: 4, position: "relative" }}>
          <Button
            size="small"
            lang={otherLanguage.value}
            onClick={() => i18n.changeLanguage(otherLanguage.value)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              textTransform: "none",
            }}
          >
            {otherLanguage.label}
          </Button>
          <Box
            component="img"
            src={logo}
            alt="Bluebell"
            sx={{ display: "block", height: 110, mx: "auto" }}
          />
          <Typography
            variant="overline"
            component="p"
            align="center"
            sx={{ color: "primary.main", letterSpacing: "0.2em", mb: 3 }}
          >
            Field Alert
          </Typography>
          <Typography variant="h5" align="center" gutterBottom>
            {t("login.title")}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            {t("login.intro")}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label={t("login.username")}
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
            <TextField
              label={t("login.password")}
              type="password"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t("login.submit")}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
